import React, { useEffect, useState } from "react";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import InputField from "components/fields/InputField";
import FileInputField from "components/fields/FileInputField";
import Modal from "components/modal";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { addContainerItems } from "store/containerSlice";
import { mediaUpload } from "api/usersApi";
import { showToast } from "components/toast";

function AddItemsModal({ refreshList }) {
  const dispatch = useDispatch();
  const { containerDetailsData, addContainerLoading } = useSelector(
    (state) => state.containers
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isSave, setIsSave] = useState(null);

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    reset();
    setIsOpen(false);
    setIsSave(null);
  };
  const closeModalMore = () => {
    reset();
    setIsSave(null);
  };

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      imgFile: null,
      description: "",
      marka: "",
      ctn: "",
      qty: "",
      totalQty: "", // auto
      rate: "",
      totalAmount: "", // auto
      cbm: "",
      totalCBM: "", // auto
      kg: "",
      totalKg: "", // auto
    },
    mode: "onBlur",
    shouldUnregister: false,
  });

  // watchers for auto-calcs
  const wCtn = watch("ctn");
  const wQty = watch("qty");
  const wRate = watch("rate");
  const wCbm = watch("cbm");
  const wKg = watch("kg");

  useEffect(() => {
    setValue("totalQty", num(wCtn) * num(wQty));
  }, [wCtn, wQty, setValue]);

  useEffect(() => {
    setValue("totalAmount", num(wRate) * num(wCtn) * num(wQty));
  }, [wRate, wCtn, wQty, setValue]);

  useEffect(() => {
    setValue("totalCBM", num(wCbm) * num(wCtn));
  }, [wCbm, wCtn, setValue]);

  useEffect(() => {
    setValue("totalKg", num(wKg) * num(wCtn));
  }, [wKg, wCtn, setValue]);

  const onSubmit = async (data) => {
    try {
      let pictureUrl = null;
      if (data.imgFile && data.imgFile[0]) {
        const res = await mediaUpload(data.imgFile[0]);
        if (res?.statuscode === 200 || res?.statuscode === 201) {
          pictureUrl = res?.data;
        } else {
          showToast.error("Image upload failed");
          return;
        }
      }

      const item = {
        name: data.name,
        picture: pictureUrl,
        description: data.description,
        marka: data.marka,
        ctn: num(data.ctn),
        qty: num(data.qty),
        totalQty: num(data.totalQty),
        rate: num(data.rate),
        totalAmount: num(data.totalAmount),
        cbm: num(data.cbm),
        totalCBM: num(data.totalCBM),
        kg: num(data.kg),
        totalKg: num(data.totalKg),
      };

      const payload = {
        containerId: containerDetailsData?._id,
        items: [item],
      };

      const res2 = await dispatch(addContainerItems(payload));
      if (
        res2?.payload?.statuscode === 200 ||
        res2?.payload?.statuscode === 201
      ) {
        refreshList?.();
        if (isSave === "saveAndMore") {
          closeModalMore();
        } else {
          closeModal();
        }
      } else {
        console.error("Add items failed:", res2?.payload?.msg || res2);
      }
    } catch (e) {
      console.error("Add items failed:", e);
    }
  };

  const ReadonlyBox = ({ label, value, id }) => {
    const base =
      "mt-0.5 flex h-9 w-full rounded-md border bg-white/0 p-3 text-sm outline-none border-gray-200 dark:!border-white/10 dark:text-white";
    return (
      <div>
        {label && (
          <label
            htmlFor={id}
            className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white"
          >
            {label}
          </label>
        )}
        <div id={id} className={base}>
          {value ?? 0}
        </div>
      </div>
    );
  };

  return (
    <>
      <Button onClick={openModal}>Load Items</Button>

      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-6xl">
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Add Container Items
        </h2>

        <div className="custom-scrollbar mt-5 max-h-[70vh] overflow-y-auto">
          <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
            {/* Editable fields */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <FileInputField
                id="imgFile"
                label="Upload Image*"
                accept="image/*"
                error={errors.imgFile}
                {...register("imgFile", { required: "Image is required" })}
              />
              <InputField
                id="name"
                label="Name*"
                variant="auth"
                type="text"
                placeholder="e.g., Phone"
                error={errors.name}
                {...register("name", { required: "Name is required" })}
              />

              <InputField
                id="description"
                label="Description*"
                variant="auth"
                type="text"
                placeholder="Item description"
                error={errors.description}
                {...register("description")}
              />

              <InputField
                id="marka"
                label="Marka*"
                variant="auth"
                type="text"
                placeholder="Brand / Marka"
                error={errors.marka}
                {...register("marka", { required: "Marka is required" })}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
              <InputField
                id="ctn"
                label="CTN*"
                variant="auth"
                type="number"
                placeholder="0"
                error={errors.ctn}
                {...register("ctn", {
                  required: "CTN is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="qty"
                label="QTY (per CTN)*"
                variant="auth"
                type="number"
                placeholder="0"
                error={errors.qty}
                {...register("qty", {
                  required: "QTY is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="rate"
                label="Rate*"
                variant="auth"
                type="number"
                step="0.01"
                placeholder="0.00"
                error={errors.rate}
                {...register("rate", {
                  required: "Rate is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="cbm"
                label="CBM (per CTN)*"
                variant="auth"
                type="number"
                step="0.0001"
                placeholder="0.0000"
                error={errors.cbm}
                {...register("cbm", {
                  required: "CBM is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="kg"
                label="KG (per CTN)*"
                variant="auth"
                type="number"
                step="0.01"
                placeholder="0.00"
                error={errors.kg}
                {...register("kg", {
                  required: "KG is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>

            {/* Readonly values section at bottom */}
            <div className=" grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <ReadonlyBox label="Total QTY (auto)" value={watch("totalQty")} />
              <input
                id="totalQty"
                type="hidden"
                {...register("totalQty", { valueAsNumber: true })}
              />

              <ReadonlyBox
                label="Total Amount (auto)"
                value={watch("totalAmount")}
              />
              <input
                id="totalAmount"
                type="hidden"
                {...register("totalAmount", { valueAsNumber: true })}
              />

              <ReadonlyBox label="Total CBM (auto)" value={watch("totalCBM")} />
              <input
                id="totalCBM"
                type="hidden"
                {...register("totalCBM", { valueAsNumber: true })}
              />

              <ReadonlyBox label="Total KG (auto)" value={watch("totalKg")} />
              <input
                id="totalKg"
                type="hidden"
                {...register("totalKg", { valueAsNumber: true })}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 py-4">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <LoaderButton
                type="submit"
                isLoading={addContainerLoading && isSave === "save"}
                onClick={() => setIsSave("save")}
              >
                Save
              </LoaderButton>
              <LoaderButton
                type="submit"
                isLoading={addContainerLoading && isSave === "saveAndMore"}
                onClick={() => setIsSave("saveAndMore")}
              >
                Save And Load
              </LoaderButton>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}

export default AddItemsModal;
